import React, { useCallback, useEffect, useState } from 'react';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import { Field, Input } from '../../ui/Field/Field';
import { finesService, errorMessage } from '../../services/finesService';

/**
 * Administration > E-mail report - the sender (owner, 4 Nov 2026: "ينفع اعمل اميل بالدومين الجديد علشان يبعت هو الرسايل
 * بدل الاميل بتاعى الشخصي ؟"): every engine e-mail (the daily report, the branch e-mails, the Brokers e-mails...) goes out
 * from yelodubai.com through Resend once its API key is saved here. The key is kept encrypted on the server and never
 * shown again; "Send a test" e-mails the admin pressing from the sender in use now.
 */
const DEFAULT_FROM = 'Yelo Dubai <noreply@yelodubai.com>';
const DEFAULT_REPLY_TO = 'admin@yelodubai.com';
const when = (iso) => String(iso || '').replace('T', ' ').slice(0, 16);

const MailSenderCard = ({ onChanged }) => {
    const [info, setInfo] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [open, setOpen] = useState(false);
    const [apiKey, setApiKey] = useState('');
    const [from, setFrom] = useState(DEFAULT_FROM);
    const [replyTo, setReplyTo] = useState(DEFAULT_REPLY_TO);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);

    // `fields`: the From / Reply-To boxes take the server's values - on the first read and after a save, never after
    // a refused save (what the admin typed stays to be corrected - review, 4 Nov 2026)
    const load = useCallback(async (fields = true) => {
        try {
            const i = await finesService.mailSenderGet();
            setInfo(i); setLoadError('');
            if (fields) { setFrom(i.from || DEFAULT_FROM); setReplyTo(i.replyTo ?? DEFAULT_REPLY_TO); }
            setOpen((o) => o || !i.configured || !!i.unreadable);
        } catch (e) { setLoadError(errorMessage(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const run = async (fn, done) => {
        setBusy(true); setNotice(null);
        let ok = false;
        try { setNotice((await fn()) || done); ok = true; } catch (e) { setNotice(errorMessage(e)); }
        finally { setBusy(false); load(ok); onChanged?.(); }
    };
    const save = () => run(async () => {
        await finesService.mailSenderSave({ apiKey: apiKey.trim(), from: from.trim(), replyTo: replyTo.trim() });
        setApiKey(''); setOpen(false);
    }, 'Saved - Resend took the key. Press Send a test to try it.');
    const test = () => run(async () => {
        const out = await finesService.mailSenderTest();
        return `Test e-mail sent to ${out.to} from ${out.from}.`;
    });
    const remove = () => run(async () => { await finesService.mailSenderRemove(); },
        'Removed - the e-mails go out from the Gmail box kept for the RTA code again.');

    return (
        <Card title="Sender · yelodubai.com"
            subtitle="Every e-mail the engine sends goes out from Yelo's own domain (Resend) once its API key is saved here.">
            <div className="grid gap-x-4 gap-y-1 text-sm mb-4" style={{ gridTemplateColumns: 'max-content 1fr' }}>
                <span className="font-semibold text-gray-700">State</span>
                <span className={info?.unreadable ? 'text-red-700 font-medium' : info?.configured ? 'text-green-700 font-medium' : 'text-amber-800 font-medium'}>
                    {!info ? (loadError ? `Not read: ${loadError}` : 'Loading…')
                        : info.unreadable ? 'The saved key cannot be read on the server - no e-mail goes out until it is saved again'
                            : info.configured ? `In use · saved ${when(info.savedAt)}${info.savedBy ? ` by ${info.savedBy}` : ''}`
                                : 'Not set - the e-mails go out from the Gmail box kept for the RTA code'}
                </span>
                {info?.configured && (<>
                    <span className="font-semibold text-gray-700">From</span>
                    <span className="text-gray-900">{info.from}</span>
                    <span className="font-semibold text-gray-700">Replies go to</span>
                    <span className="text-gray-900">{info.replyTo || '—'}</span>
                    <span className="font-semibold text-gray-700">Last test</span>
                    <span className={info.testError ? 'text-red-700 font-medium' : 'text-gray-900'}>
                        {!info.testedAt ? 'Not tried yet' : info.testError ? `${when(info.testedAt)} · failed: ${info.testError}` : `${when(info.testedAt)} · sent`}
                    </span>
                </>)}
            </div>
            {open ? (
                <div className="flex flex-col gap-3">
                    <Field label="Resend API key (Resend > API Keys > Create API key, Sending access)">
                        <Input type="password" autoComplete="new-password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                            placeholder="re_..." aria-label="Resend API key" className="w-full" />
                    </Field>
                    <div className="flex flex-wrap gap-3">
                        <Field label="From" className="flex-1 min-w-[240px]">
                            <Input value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" className="w-full" />
                        </Field>
                        <Field label="Replies go to" className="flex-1 min-w-[200px]">
                            <Input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} aria-label="Reply-To" className="w-full" />
                        </Field>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button variant="primary" onClick={save} disabled={busy || !apiKey.trim()} isLoading={busy}>Save key</Button>
                        {info?.configured && <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>}
                        <span className="text-xs text-gray-500">The key is kept encrypted on the server and never shown again.</span>
                    </div>
                </div>
            ) : (
                <div className="flex flex-wrap gap-2">
                    <Button variant="primary" onClick={test} disabled={busy} isLoading={busy}
                        leadingIcon={<Icon glyph={ICONS.check} size="sm" />}>Send a test to me</Button>
                    <Button variant="secondary" onClick={() => setOpen(true)} disabled={busy}>Change key</Button>
                    <Button variant="ghost" onClick={remove} disabled={busy}
                        leadingIcon={<Icon glyph={ICONS.close} size="sm" />}>Stop using it</Button>
                </div>
            )}
            {notice && <div className="mt-4 rounded-lg bg-gray-100 px-4 py-3 text-sm text-gray-800" role="status" aria-live="polite">{notice}</div>}
        </Card>
    );
};

export default MailSenderCard;
